import type { ConnectOutcome, TrackerConnection } from '@forecastify/shared';
import { Button, Icon, cn } from '@forecastify/ui';
import { useT } from '../../i18n';
import type { MessageKey } from '../../i18n/catalogue';
import type { ConnectReturnRead } from '../integrations/connect-return';
import { ProviderLogo } from '../integrations/provider-logo';
import { Outcome, type OutcomeTone } from '../outcome';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 26 Oct 2026 · back from Atlassian: connected, or why not.                 │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * A person connecting their own Jira account leaves for Atlassian's consent screen and is
 * redirected back. That return was a toast; it is a page now, in the account screen's pane, until
 * they press *Kész* / *Vissza*.
 *
 * ⚠️⚠️ **`wrong_site` IS DRAWN APART FROM EVERY OTHER FAILURE, AND IT IS THE POINT.** Atlassian's
 * consent screen lists every site the account can reach and cannot be told which one to show; a
 * person with two Jiras picks one, and if it is not this instance's the connect fails. It is the one
 * failure they cause and can fix themselves, so the page puts what they picked beside what this
 * workspace uses — the instruction is the comparison — and *Try again* sits under it. The others
 * get a sentence each, and the provider's own words when it gave any.
 *
 * ⚠️ **Try again only where it can work**: never without the instance (a state that did not verify
 * names none), and never for `not_configured` or `not_found`, which another attempt meets again.
 */
const TITLE: Record<ConnectOutcome, MessageKey> = {
  ok: 'account.connections.outcome.ok.title',
  cancelled: 'account.connections.outcome.cancelled.title',
  refused: 'account.connections.outcome.refused.title',
  state: 'account.connections.outcome.state.title',
  expired: 'account.connections.outcome.expired.title',
  wrong_site: 'account.connections.outcome.wrong_site.title',
  no_refresh: 'account.connections.outcome.no_refresh.title',
  not_configured: 'account.connections.outcome.not_configured.title',
  not_found: 'account.connections.outcome.not_found.title',
  malformed: 'account.connections.outcome.malformed.title',
  failed: 'account.connections.outcome.failed.title',
};

const BODY: Record<ConnectOutcome, MessageKey> = {
  ok: 'account.connections.outcome.ok.body',
  cancelled: 'account.connections.outcome.cancelled.body',
  refused: 'account.connections.outcome.refused.body',
  state: 'account.connections.outcome.state.body',
  expired: 'account.connections.outcome.expired.body',
  wrong_site: 'account.connections.outcome.wrong_site.body',
  no_refresh: 'account.connections.outcome.no_refresh.body',
  not_configured: 'account.connections.outcome.not_configured.body',
  not_found: 'account.connections.outcome.not_found.body',
  malformed: 'account.connections.outcome.malformed.body',
  failed: 'account.connections.outcome.failed.body',
};

/** Another attempt meets the same answer for these, so none is offered. */
const FINAL: ReadonlySet<ConnectOutcome> = new Set(['ok', 'not_configured', 'not_found']);

const toneOf = (outcome: ConnectOutcome): OutcomeTone =>
  outcome === 'ok' ? 'ok' : outcome === 'wrong_site' || outcome === 'cancelled' ? 'warn' : 'bad';

const hostOf = (url: string): string => {
  try {
    return new URL(url).host.toLowerCase();
  } catch {
    return url;
  }
};

export function ConnectResult({
  result,
  connections,
  busy,
  onRetry,
  onDone,
}: {
  readonly result: ConnectReturnRead;
  /** To name the instance the flow was for. Null while it loads. */
  readonly connections: readonly TrackerConnection[] | null;
  readonly busy: boolean;
  /** Starts the same connection again — the account screen's own *Connect*. */
  readonly onRetry: (instanceId: string) => void;
  /** Back to the integrations pane. */
  readonly onDone: () => void;
}) {
  const t = useT();
  const { outcome, instanceId } = result;
  const instance =
    instanceId === null
      ? undefined
      : connections?.find((connection) => connection.instanceId === instanceId);
  /* ⚠️ The server's host first; the list's address only for a server that did not send one. */
  const expected = result.expected ?? (instance === undefined ? null : hostOf(instance.baseUrl));
  const retry = instanceId !== null && !FINAL.has(outcome) ? instanceId : null;

  const back = (
    <Button variant={retry === null ? 'default' : 'outline'} onClick={onDone}>
      {outcome === 'ok'
        ? t('account.connections.outcome.done')
        : t('account.connections.outcome.back')}
    </Button>
  );

  return (
    <Outcome
      tone={toneOf(outcome)}
      marker={outcome}
      title={t(TITLE[outcome])}
      body={t(BODY[outcome])}
      actions={
        <>
          {retry === null ? null : (
            <Button
              disabled={busy}
              onClick={() => {
                onRetry(retry);
              }}
            >
              {t('account.connections.outcome.retry')}
            </Button>
          )}
          {back}
        </>
      }
    >
      {outcome === 'wrong_site' ? (
        /* ⚠️ The hosts WRAP rather than truncate — the address is the instruction, and a phone
           cutting it to *estratos-consulting-gr…* would hide the part that tells two sites apart.
           The label keeps its line; when both do not fit, the host drops under it. */
        <dl className="rounded-lg border text-left text-[13px]">
          <div
            className={cn(
              'flex flex-wrap items-center justify-between gap-x-3 gap-y-1 px-3 py-2.5',
              expected === null ? null : 'border-b',
            )}
          >
            <dt className="text-muted-foreground text-[11px] font-medium tracking-wide whitespace-nowrap uppercase">
              {t('account.connections.outcome.wrong_site.picked')}
            </dt>
            <dd className="ml-auto flex min-w-0 flex-col items-end gap-0.5">
              {result.picked.length === 0 ? (
                /* The design system's mark for an absent value. */
                <span className="text-muted-foreground">–</span>
              ) : (
                result.picked.map((host) => (
                  <span key={host} className="flex max-w-full items-center gap-1.5">
                    <Icon name="x" size={13} className="text-status-bad-text shrink-0" />
                    <span className="min-w-0 [overflow-wrap:anywhere]">{host}</span>
                  </span>
                ))
              )}
            </dd>
          </div>
          {expected === null ? null : (
            <div className="bg-status-ok-soft flex flex-wrap items-center justify-between gap-x-3 gap-y-1 rounded-b-lg px-3 py-2.5">
              <dt className="text-muted-foreground text-[11px] font-medium tracking-wide whitespace-nowrap uppercase">
                {t('account.connections.outcome.wrong_site.expected')}
              </dt>
              <dd className="ml-auto flex max-w-full min-w-0 items-center gap-1.5 font-medium">
                <Icon name="circle-check" size={13} className="text-status-ok-text shrink-0" />
                <span className="min-w-0 [overflow-wrap:anywhere]">{expected}</span>
              </dd>
            </div>
          )}
        </dl>
      ) : instance === undefined ? null : (
        /* Which Jira this was about — the name the workspace gave it, and its address. */
        <div className="text-muted-foreground flex items-center justify-center gap-2 text-[12.5px]">
          <ProviderLogo provider={instance.extensionId} size={14} />
          <span className="text-foreground truncate">{instance.name}</span>
          <span aria-hidden>·</span>
          <span className="truncate">{hostOf(instance.baseUrl)}</span>
        </div>
      )}
      {/* Not on a cancel: Atlassian's `access_denied` only repeats what the person just did. */}
      {result.detail === null || result.detail === '' || outcome === 'cancelled' ? null : (
        /* ⚠️ The provider's own words, untranslated on purpose — what somebody searches for. */
        <p className="bg-muted/40 text-muted-foreground mt-3 rounded-md px-3 py-2 text-left text-[12px] leading-relaxed break-words">
          <span className="text-foreground font-medium">
            {t('account.connections.outcome.detail')}
          </span>{' '}
          {result.detail}
        </p>
      )}
    </Outcome>
  );
}
